// ADV-1157: renders a structured import diagnosis — root cause card, cited
// evidence pills, and one-click remediation. Operator-grade register (ADR-0019
// §4/§5): declarative, verb-labelled actions, no SaaS warmth. Tokens only
// (ADR-0018) — category colour comes from semantic CSS vars, not raw hex.
//
// ADV-1165: dual register. `register="operator"` (super-admins) keeps the full
// operator-grade view — internal references, taxonomy labels, model metadata.
// `register="user"` (tenant back-office staff) renders the plain-language
// `user_summary`, only user-safe evidence (no `font-mono` internal refs — §C3.1
// rationale), a non-alarming category label (no "Platform bug"), and hides the
// model/cost footer and the operator-only File-ticket action. Same operator
// register otherwise — not "friendlier", just infra-vocabulary stripped.

import type {
  ImportDiagnosis,
  ImportDiagnosisCategory,
  ImportDiagnosisEvidenceKind,
  ImportDiagnosisRemediationAction,
} from "@adva/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const CATEGORY_LABEL: Record<ImportDiagnosisCategory, string> = {
  data: "Data issue",
  platform: "Platform bug",
  config: "Configuration",
  transient: "Transient",
};

// ADV-1165: user register labels — no "Platform bug" (internal blame framing).
// A platform-category failure is "nothing for the user to change", so it reads
// as a neutral status, not an alarm.
const USER_CATEGORY_LABEL: Record<ImportDiagnosisCategory, string> = {
  data: "Issue in the uploaded file",
  platform: "Logged with Adva",
  config: "Import setup issue",
  transient: "Temporary issue",
};

// Semantic token per category — diverging from the chart ring (ADR-0020 N/A
// here); a category is a status signal, not a series.
const CATEGORY_TOKEN: Record<ImportDiagnosisCategory, string> = {
  data: "var(--color-warning)",
  platform: "var(--color-danger)",
  config: "var(--color-accent)",
  transient: "var(--color-text-md)",
};

// ADV-1165: user register tokens — `platform` is neutral/muted (nothing for the
// user to act on), never danger-red.
const USER_CATEGORY_TOKEN: Record<ImportDiagnosisCategory, string> = {
  data: "var(--color-warning)",
  platform: "var(--color-text-md)",
  config: "var(--color-accent)",
  transient: "var(--color-text-md)",
};

const EVIDENCE_LABEL: Record<ImportDiagnosisEvidenceKind, string> = {
  log: "Log",
  step: "Step",
  error_row: "Error row",
  reconciliation: "Reconciliation",
  csv_row: "CSV row",
};

// ADV-1165: only these evidence kinds may ever reach the user register —
// they describe the user's own uploaded data. `log`/`step`/`reconciliation`
// expose internal taxonomy/identifiers and are structurally excluded here,
// independent of the model-set `user_safe` flag (defense in depth).
const USER_SAFE_KINDS: ReadonlySet<ImportDiagnosisEvidenceKind> = new Set([
  "csv_row",
  "error_row",
]);

const REMEDIATION_LABEL: Record<ImportDiagnosisRemediationAction, string> = {
  retry_normalization: "Retry normalization",
  cancel_job: "Cancel job",
  fix_csv_and_resubmit: "Fix CSV & re-import",
  file_linear_ticket: "File Linear ticket",
  contact_support: "Contact support",
  wait: "Wait and retry later",
};

interface DiagnosisRemediationHandlers {
  onRetry?: () => void;
  onCancel?: () => void;
  onFileTicket?: () => void;
  retrying?: boolean;
  cancelling?: boolean;
  filingTicket?: boolean;
  /** Whether the caller may file a Linear ticket (super-admin / owner). */
  canFileTicket?: boolean;
  /** Whether the job is in a retryable state (failed / expired). */
  canRetry?: boolean;
}

export function DiagnosisResult({
  diagnosis,
  handlers,
  register = "operator",
}: {
  diagnosis: ImportDiagnosis;
  handlers: DiagnosisRemediationHandlers;
  /** Which register to render. `operator` (super-admins) keeps the full
   * operator-grade view; `user` (tenant back-office staff) strips internal
   * references, taxonomy, and model metadata. Defaults to operator. */
  register?: "operator" | "user";
}) {
  const isUser = register === "user";
  const category = diagnosis.root_cause.category;
  const token = isUser
    ? USER_CATEGORY_TOKEN[category]
    : CATEGORY_TOKEN[category];
  const label = isUser
    ? USER_CATEGORY_LABEL[category]
    : CATEGORY_LABEL[category];
  // Fallback to the operator summary if a (pre-deploy, ≤60s-cached) payload
  // lacks user_summary — the field is required on fresh diagnoses.
  const summary =
    isUser && diagnosis.root_cause.user_summary
      ? diagnosis.root_cause.user_summary
      : diagnosis.root_cause.summary;
  // Users only ever see evidence the model flagged as safe AND of a safe kind
  // (csv_row / error_row) — log/step/reconciliation are excluded structurally.
  const evidence = isUser
    ? diagnosis.evidence.filter(
        (e) => e.user_safe && USER_SAFE_KINDS.has(e.kind),
      )
    : diagnosis.evidence;

  return (
    <div className="space-y-4">
      {/* Root cause */}
      <div
        className="rounded-lg border bg-[color:var(--color-surface)] p-4"
        style={{ borderColor: token }}
      >
        <div className="mb-2 flex items-center gap-2">
          <Badge variant="outline" style={{ color: token, borderColor: token }}>
            {label}
          </Badge>
          <span className="text-sm text-[color:var(--color-text-md)]">
            {diagnosis.retry_safe ? "Safe to retry" : "Not safe to retry as-is"}
          </span>
        </div>
        <p className="text-sm text-[color:var(--color-text-hi)]">{summary}</p>
      </div>

      {/* Evidence — operator sees taxonomy + internal references; users see
          only user-safe plain quotes (no kind badge, no font-mono ref). */}
      {evidence.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-[color:var(--color-text-md)]">
            {isUser ? "Details" : "Evidence"}
          </h4>
          <ul className="space-y-2">
            {evidence.map((e, i) => (
              <li key={`${e.reference}-${i}`} className="flex gap-2 text-sm">
                {!isUser && (
                  <Badge variant="secondary" className="shrink-0">
                    {EVIDENCE_LABEL[e.kind]}
                  </Badge>
                )}
                <span className="min-w-0">
                  {!isUser && (
                    <span className="font-mono text-xs text-[color:var(--color-text-md)]">
                      {e.reference}
                    </span>
                  )}
                  <span className="block text-[color:var(--color-text-hi)]">
                    {e.quote}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Remediation */}
      {diagnosis.suggested_remediation.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-[color:var(--color-text-md)]">
            Suggested remediation
          </h4>
          <ul className="space-y-3">
            {diagnosis.suggested_remediation.map((r, i) => (
              <li key={`${r.action}-${i}`} className="space-y-1">
                <p className="text-sm text-[color:var(--color-text-hi)]">
                  <span className="font-medium">
                    {REMEDIATION_LABEL[r.action]}
                  </span>{" "}
                  — {r.rationale}
                </p>
                {renderRemediationButton(r.action, handlers, register)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Model metadata — operator-only muted footer (tokens/cost is operator
          detail; users never see it). */}
      {!isUser && (
        <>
          <Separator />
          <p className="text-xs text-[color:var(--color-text-md)]">
            {diagnosis.model_metadata.model} ·{" "}
            {diagnosis.model_metadata.input_tokens +
              diagnosis.model_metadata.output_tokens}{" "}
            tokens · ${diagnosis.model_metadata.cost_usd.toFixed(4)}
          </p>
        </>
      )}
    </div>
  );
}

function renderRemediationButton(
  action: ImportDiagnosisRemediationAction,
  h: DiagnosisRemediationHandlers,
  register: "operator" | "user",
) {
  switch (action) {
    case "retry_normalization":
      if (!h.onRetry || !h.canRetry) return null;
      return (
        <Button size="sm" onClick={h.onRetry} disabled={h.retrying}>
          {h.retrying ? "Retrying…" : "Retry normalization"}
        </Button>
      );
    case "cancel_job":
      if (!h.onCancel) return null;
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={h.onCancel}
          disabled={h.cancelling}
        >
          {h.cancelling ? "Cancelling…" : "Cancel job"}
        </Button>
      );
    case "file_linear_ticket":
      // Operator-only: files an operator-grade internal ticket. Never shown in
      // the user register (ADV-1165), even if a handler/ability is passed.
      if (register !== "operator" || !h.onFileTicket || !h.canFileTicket)
        return null;
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={h.onFileTicket}
          disabled={h.filingTicket}
        >
          {h.filingTicket ? "Filing…" : "File Linear ticket"}
        </Button>
      );
    default:
      return null;
  }
}
